import React, { useState } from 'react';
import {
   Container,
   Typography,
   Paper,
   Box,
   Divider,
   List,
   ListItem,
   ListItemText,
   AppBar,
   Toolbar,
   Button,
   IconButton,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';

const TermsPage: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const navigate = useNavigate();
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
         {/* Navigation */}
         <AppBar
            position='fixed'
            sx={{
               background:
                  'linear-gradient(135deg, rgba(1, 148, 154, 0.95) 0%, rgba(0, 67, 105, 0.95) 100%)',
               backdropFilter: 'blur(15px)',
               boxShadow: '0 2px 15px rgba(1, 148, 154, 0.2)',
               zIndex: 1100,
               color: 'white',
               border: 'none',
               borderRadius: 0,
            }}
         >
            <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
               <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => navigate('/')}
               >
                  <img
                     src={logo}
                     alt='WeTrippen Logo'
                     style={{ width: 32, height: 32, borderRadius: 6 }}
                  />
                  <Typography
                     variant='h6'
                     sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}
                  >
                     WeTrippen
                  </Typography>
               </Box>

               {!isMobile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                     <Button
                        component={RouterLink}
                        to='/login'
                        sx={{
                           color: 'rgba(255,255,255,0.9)',
                           textTransform: 'none',
                           fontSize: '0.9rem',
                           fontWeight: 400,
                        }}
                     >
                        Sign In
                     </Button>
                     <Button
                        component={RouterLink}
                        to='/signup'
                        variant='contained'
                        sx={{
                           background: 'rgba(255,255,255,0.2)',
                           color: 'white',
                           textTransform: 'none',
                           fontWeight: 500,
                           borderRadius: 2,
                           px: 2.5,
                           py: 0.5,
                           fontSize: '0.9rem',
                           backdropFilter: 'blur(10px)',
                           border: '1px solid rgba(255,255,255,0.3)',
                           '&:hover': {
                              background: 'rgba(255,255,255,0.3)',
                              transform: 'translateY(-1px)',
                           },
                        }}
                     >
                        Get Started
                     </Button>
                  </Box>
               ) : (
                  <IconButton
                     onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                     sx={{ color: 'white' }}
                  >
                     {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
               )}
            </Toolbar>

            {/* Mobile Menu */}
            {isMobile && mobileMenuOpen && (
               <Box sx={{ bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Button
                     fullWidth
                     component={RouterLink}
                     to='/login'
                     sx={{ justifyContent: 'flex-start', mb: 1, color: 'text.primary' }}
                  >
                     Sign In
                  </Button>
                  <Button
                     fullWidth
                     component={RouterLink}
                     to='/signup'
                     variant='contained'
                     sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                     Get Started
                  </Button>
               </Box>
            )}
         </AppBar>

         <Container maxWidth='lg' sx={{ pt: 12, pb: 4 }}>
            <Typography
               variant='h3'
               component='h1'
               gutterBottom
               align='center'
               sx={{ mb: 2, fontWeight: 700 }}
            >
               TERMS OF SERVICE
            </Typography>

            <Typography
               variant='body1'
               color='text.secondary'
               align='center'
               sx={{ mb: 4, fontWeight: 500 }}
            >
               Effective Date: January 15, 2025
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  1. ACCEPTANCE OF TERMS
               </Typography>
               <Typography variant='body1' paragraph>
                  These Terms of Service ("Terms") govern your use of WeTrippen, a service provided
                  by Align Workspace Inc., a Delaware corporation ("Company," "we," "us," or "our").
                  By accessing or using WeTrippen ("Service"), you agree to be bound by these Terms
                  and our Privacy Policy, which is incorporated herein by reference.
               </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  2. DESCRIPTION OF SERVICE
               </Typography>
               <Typography variant='body1' paragraph>
                  WeTrippen is a digital platform that enables users to:
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Create and manage local events and community gatherings' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Upload, organize, and share photos and albums' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Use AI-powered photo enhancement and editing tools' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Communicate with other users through chat and messaging features' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Access calendar and scheduling tools' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Utilize map and location-based services' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Manage user profiles and social connections' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  3. USER ACCOUNTS AND REGISTRATION
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  3.1 Account Creation
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You must provide accurate, current, and complete information during registration' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You are responsible for maintaining the confidentiality of your account credentials' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You must be at least 13 years old to create an account' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• One account per person; multiple accounts are prohibited' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  3.2 Account Security
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You are responsible for all activities that occur under your account' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You must notify us immediately of any unauthorized use of your account' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We are not liable for any loss or damage arising from unauthorized account use' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  4. SUBSCRIPTION AND PAYMENT TERMS
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  4.1 Personal Account Subscription
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Personal accounts are available for $35.00 per year' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Payment is processed through Stripe and is generally non-refundable except as required by law or in cases of service failure' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Subscriptions automatically renew unless cancelled before the renewal date' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You may cancel your subscription at any time through your account settings' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  4.2 Payment Processing
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• All payments are processed securely through third-party payment processors' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You authorize us to charge your payment method for subscription fees' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Failed payments may result in service suspension after reasonable notice' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary="• Prices are subject to change with 30 days' written notice" />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  4.3 Refund Policy
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Refunds may be provided in cases of technical service failure lasting more than 7 consecutive days' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Refunds for other circumstances will be considered on a case-by-case basis' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• No refunds will be provided for partial subscription periods or unused features' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  5. USER CONTENT AND INTELLECTUAL PROPERTY
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  5.1 Your Content
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary="• You retain ownership of all photos, videos, and other content you upload ('User Content')" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• By uploading content, you grant us a non-exclusive, royalty-free license to store, display, and process your content solely to provide the Service' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You represent that you have all necessary rights to upload and share your content' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You are responsible for ensuring you have permission to upload photos containing other individuals' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  5.2 Content Restrictions
               </Typography>
               <Typography variant='body1' paragraph>
                  You may not upload content that:
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Violates any laws or regulations' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Infringes on intellectual property rights of others' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Contains harmful, offensive, or inappropriate material' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Includes personal information of others without consent' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Contains malware, viruses, or other harmful code' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Depicts illegal activities or promotes violence' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  5.3 AI-Enhanced Content
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• AI-enhanced photos remain your property' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may use anonymized, non-identifiable data from AI processing to improve our services' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You are responsible for ensuring AI-enhanced content complies with these Terms' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• AI enhancement of copyrighted material does not grant additional rights' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  5.4 Content Moderation
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We reserve the right to review and remove content that violates these Terms' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Content removal decisions may be appealed by contacting support' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Repeated violations may result in account suspension or termination' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  6. PRIVACY AND DATA PROTECTION
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  6.1 Data Collection
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We collect and process personal data as described in our Privacy Policy' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We implement appropriate security measures to protect your data' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may use your data to provide, improve, and personalize our services' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  6.2 Data Sharing
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We do not sell your personal data to third parties' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may share data with service providers who assist in operating our platform' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may disclose data when required by law or to protect our rights' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  6.3 Data Retention
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We retain user data for as long as your account is active' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• After account deletion, we retain data for 90 days for backup purposes, then permanently delete it' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Some data may be retained longer if required by law' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  7. PROHIBITED USES
               </Typography>
               <Typography variant='body1' paragraph>
                  You agree not to:
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Use the Service for any unlawful purpose' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Attempt to gain unauthorized access to our systems' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Interfere with or disrupt the Service' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Create fake accounts or impersonate others' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Spam or send unsolicited communications' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Use automated tools to access the Service without permission' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Reverse engineer or attempt to extract source code' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Use the Service to harass, abuse, or harm others' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  8. INTELLECTUAL PROPERTY RIGHTS
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  8.1 Our Rights
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• WeTrippen and all related trademarks, logos, and content are owned by Align Workspace Inc.' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• The Service, including its design, functionality, and features, is protected by intellectual property laws' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You may not copy, modify, or distribute our proprietary content' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  8.2 Third-Party Services
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Our Service integrates with third-party services (Stripe, Firebase, Mapbox, etc.)' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Use of third-party services is subject to their respective terms and conditions' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We are not responsible for third-party service availability or functionality' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  9. SERVICE AVAILABILITY AND MODIFICATIONS
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  9.1 Service Availability
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We strive to maintain 99% service availability but do not guarantee uninterrupted access' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may perform scheduled maintenance with reasonable advance notice' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We are not liable for service interruptions due to circumstances beyond our reasonable control' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  9.2 Service Modifications
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary="• We may modify, update, or discontinue features of the Service with 30 days' notice for material changes" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Continued use after modifications constitutes acceptance of changes' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We will not materially reduce core functionality without offering alternatives' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  10. TERMINATION
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  10.1 Termination by You
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You may terminate your account at any time through account settings' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Upon termination, your access to the Service will cease immediately' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We will retain data according to our retention policy outlined in Section 6.3' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  10.2 Termination by Us
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may suspend or terminate accounts that violate these Terms after appropriate warning' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may immediately terminate accounts for severe violations or fraudulent activity' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary="• We will provide 30 days' notice for terminations not related to Terms violations" />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  11. DISCLAIMERS AND LIMITATIONS OF LIABILITY
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  11.1 Service Disclaimers
               </Typography>
               <Typography
                  variant='body1'
                  paragraph
                  sx={{ fontWeight: 500, textTransform: 'uppercase' }}
               >
                  THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
                  WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A
                  PARTICULAR PURPOSE. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR
                  UNINTERRUPTED.
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  11.2 Limitation of Liability
               </Typography>
               <Typography
                  variant='body1'
                  paragraph
                  sx={{ fontWeight: 500, textTransform: 'uppercase' }}
               >
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR LIABILITY IS LIMITED TO THE AMOUNT YOU
                  PAID FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM. WE ARE NOT LIABLE FOR
                  INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES. SOME JURISDICTIONS DO NOT
                  ALLOW LIMITATIONS OF LIABILITY, SO THESE LIMITATIONS MAY NOT APPLY TO YOU.
               </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  12. INDEMNIFICATION
               </Typography>
               <Typography variant='body1' paragraph>
                  You agree to indemnify and hold harmless Align Workspace Inc. and its officers,
                  directors, employees, and agents from any claims, damages, or expenses arising
                  from:
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Your use of the Service' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Your violation of these Terms' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Your violation of any third-party rights' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Your User Content' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  13. GOVERNING LAW AND DISPUTE RESOLUTION
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  13.1 Governing Law
               </Typography>
               <Typography variant='body1' paragraph>
                  These Terms are governed by the laws of the State of Delaware and the Commonwealth
                  of Pennsylvania, without regard to conflict of law principles.
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  13.2 Dispute Resolution
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Any disputes will first be addressed through good faith negotiations' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• If unresolved after 60 days, disputes may be resolved through binding arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Arbitration will be conducted in Philadelphia, Pennsylvania' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Small claims court actions are excluded from this arbitration requirement' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• Class action lawsuits are prohibited, but this provision is severable if found unenforceable' />
                  </ListItem>
               </List>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  14. GENERAL PROVISIONS
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  14.1 Entire Agreement
               </Typography>
               <Typography variant='body1' paragraph>
                  These Terms, together with our Privacy Policy, constitute the entire agreement
                  between you and us.
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  14.2 Severability
               </Typography>
               <Typography variant='body1' paragraph>
                  If any provision of these Terms is found unenforceable, the remaining provisions
                  will remain in effect.
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  14.3 Waiver
               </Typography>
               <Typography variant='body1' paragraph>
                  Our failure to enforce any provision does not constitute a waiver of that
                  provision.
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  14.4 Assignment
               </Typography>
               <List sx={{ pl: 2 }}>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• We may assign these Terms without your consent in connection with a merger, acquisition, or sale of assets' />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                     <ListItemText primary='• You may not assign these Terms without our written consent' />
                  </ListItem>
               </List>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  14.5 Force Majeure
               </Typography>
               <Typography variant='body1' paragraph>
                  We are not liable for delays or failures due to circumstances beyond our
                  reasonable control, including but not limited to natural disasters, government
                  actions, internet service failures, cyber attacks, or other unforeseeable events.
               </Typography>

               <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  14.6 International Users
               </Typography>
               <Typography variant='body1' paragraph>
                  If you access the Service from outside the United States, you are responsible for
                  compliance with local laws and regulations.
               </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  15. CONTACT INFORMATION
               </Typography>
               <Typography variant='body1' paragraph>
                  For questions about these Terms, please contact us at:
               </Typography>
               <Box sx={{ pl: 2 }}>
                  <Typography variant='body1' paragraph>
                     <strong>Align Workspace Inc.</strong>
                     <br />
                     c/o Emmanuel Jones
                     <br />
                     Philadelphia, Pennsylvania
                     <br />
                     Email: legal@alignworkspace.com
                     <br />
                     Phone: (703) 944-3335
                  </Typography>
               </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
               >
                  16. CHANGES TO TERMS
               </Typography>
               <Typography variant='body1' paragraph>
                  We may update these Terms from time to time. We will notify users of material
                  changes via email or through the Service at least 30 days before changes become
                  effective. Your continued use after changes become effective constitutes
                  acceptance of the new Terms.
               </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography
               variant='body2'
               color='text.secondary'
               align='center'
               sx={{ fontWeight: 500 }}
            >
               Last Updated: January 15, 2025
            </Typography>
         </Container>
      </Box>
   );
};

export default TermsPage;
